<template>

    <v-container fluid class="sas-container d-flex flex-column pa-0" :style="containerStyle">

      <!-- HEADER -->
      <SmartAgentSearchBar ref="smartSearchBar"
          :activity="search.state.isActive"
           @update:state="search.state = { ...search.state, ...$event }"
           @search-results="search.results = $event"
           @search-click="onClickSearchInput"
           @quick-agent="openQuickForm"
           class="mb-4"
      />

      <!-- CONTENT -->
      <v-card class="sas-content" >

        <WorkshopInfoPanel class="sas-info-panel" v-if="shouldDisplayInfos" :can-close="showInfos"  @close-infos="$emit('close-infos')"/>

        <!-- [CONTENT] SelectAgentPanel -->
        <SelectAgentPanel
                    v-else-if="searchUiState === 'selection-panel'"
                    ref="selectAgentPanel"
                    :workshopId="this.workshopId"
                    :open="selectAgentPanel.isActive"
                    :agencyId="selectAgentPanel.agencyId"
                    :agentId="selectAgentPanel.agentId"
                    :initialSelection="selectAgentPanel.initialSelection"
                    @close="closeselectAgentPanel"
                    @select="handleSelection"
                />

        <!-- [CONTENT] QuickAgent Form -->
        <div v-else-if="searchUiState === 'quick-form'" class="sas-quick-form">
          <QuickAgentForm v-model="quickForm"
                          @cancel="closeQuickForm"
                          @confirm="handleWildQuickAgentCreation"
                          wildAgent
                          listenKeyboard
          />
        </div>

        <!-- [CONTENT] Search Live Results -->

        <SmartAgentSearchResults
            v-else-if="['loading', 'refreshing', 'empty', 'results', 'error'].includes(searchUiState)"
            :search-state="search.state"
            :agencies="search.results.agencies"
            :agents="search.results.agents"
            @select-agency="onSelectAgency"
            @select-agent="onSelectAgent"
            @open-quick-form="openQuickForm"
        />

      </v-card>
      <!-- CONTENT [END] -->

      <!-- [N] Debug -->
<!--    <div class="text-sm-caption blue&#45;&#45;text">-->
        <!--
        <p><b>selectedContacts :</b></p>
        <p v-for="(contact, index) in selectedContacts" :key="contact.id">
          [{{index + 1}}] || {{contact}}
        </p>
        -->
        <!--
        <pre>{{ JSON.stringify(convertedSelectedContacts, null, 2) }}</pre>
        -->
        <!-- -->
<!--        <p class="mt-1"><b>Search.state</b></p>
        <p v-for="(entry, index) in Object.entries(search.state)" :key="`debug-searchstate-${index}`">
          {{entry[0]}} : {{entry[1]}}
        </p>-->
<!--      </div>-->

    </v-container>
</template>


<script>
import { get } from "@/utils/api";
import FormMixin from "@/components/mixins/FormMixin";
import SelectAgentPanel from "@/components/commons/SelectAgentPanel.vue";
import QuickAgentForm from "@/components/commons/QuickAgentForm.vue";
import SmartAgentSearchBar from "@/components/commons/SmartAgentSearchBar.vue";
import SmartAgentSearchResults from "@/views/hlagons/workshop/SmartAgentSearchResults.vue";
import WorkshopInfoPanel from "@/views/hlagons/workshop/WorkshopInfoPanel.vue";

export default {
  name: "SmartAgentSearch",
  mixins: [FormMixin],
  components: {
    WorkshopInfoPanel,
    SmartAgentSearchResults,
    SmartAgentSearchBar, QuickAgentForm, SelectAgentPanel},
  props: {
    selectedContacts : {type: Array, default: () => [] },
    workshopId: { type: String, required: true },
    showInfos: { type: Boolean, default: false },
  },
  emits: ['update:activity', 'selected', 'unselected', 'close-infos'],
  data: () => ({
    search: {
      state: {
        query: "",
        rawQuery: "",
        scope: "",
        canSearch: false,
        isBusy: false,
        isActive: false,
        error: null,
        uiState: "idle",
      },
      results: {
        agents: [],
        agencies: [],
      }
    },
    showQuickForm: false,
    quickForm: {
      firstname: "",
      lastname: "",
      email: "",
      agencyName: "",
    },
    selectAgentPanel: {
      isActive: false,
      agencyId: null,
      agentId: null,
      initialSelection: null,
    },
  }),
  watch: {
    isSearchActive: {
      immediate: true,
      handler(value) {
        this.$emit('update:activity', value)
      }
    }
  },
  computed: {
    shouldDisplayInfos() {
      return (this.searchUiState === 'infos' && this.selectedContacts.length === 0) || this.showInfos
    },
    containerStyle() {
      return this.isSearchActive || this.showQuickForm || this.showInfos ? { height: "100%" } : {}
    },
    isSearchActive() {
      return this.search.state.isActive || this.selectAgentPanel.isActive
    },
    hasResults() {
      const agents = this.search.results?.agents ?? []
      const agencies = this.search.results?.agencies ?? []
      return agents.length > 0 || agencies.length > 0
    },
    searchUiState() {
      if (this.selectAgentPanel.isActive) return "selection-panel"
      if (this.showQuickForm) return "quick-form"
      if (!this.search.state.canSearch) {
        return this.search.state.isActive ? "infos" : "idle"
      }
      if (this.search.state.uiState === "loading") {
        return this.hasResults ? "refreshing" : "loading"
      }
      if (this.search.state.uiState === "error") {
        return this.hasResults ? "results" : "error"
      }
      if (!this.hasResults) return "empty"
      return "results"
    },
    convertedSelectedContacts() {
      return this.selectedContacts.map(this.convertContact)
    },
    alreadySelectedAgencies() {
      const map = {}

      this.convertedSelectedContacts.forEach(contact => {
        if (!contact.agencyId) return

        if (!map[contact.agencyId]) {
          map[contact.agencyId] = {
            agency: {
              id: contact.agencyId,
              name: contact.agencyName || ''
            },
            selectedIds: [],
            quickAgents: []
          }
        }

        if (contact.type === 'agent' && contact.agentId) {
          map[contact.agencyId].selectedIds.push(contact.agentId)
        }

        if (contact.type === 'quickAgent') {
          map[contact.agencyId].quickAgents.push({
            firstname: contact.firstname,
            lastname: contact.lastname,
            email: contact.email
          })
        }
      })

      return Object.values(map)
    },
  },
  methods: {
    convertContact(contact) {
      return {
        id: contact.id || null,
        workshopId: contact.workshopId || null,
        meetId: contact.meetId || null,
        agentId: contact.agentId || null,
        agencyId: contact.agencyId || null,
        agencyName: contact.agencyName || '',
        firstname: contact.firstname || '',
        lastname: contact.lastname || '',
        email: contact.email || '',
        type: contact.type || this.detectType(contact)
      }
    },
    detectType(contact) {
      if (contact.agencyId && contact.agentId) return 'agent'
      if (contact.agencyId) return 'quickAgent'
      return 'wildAgent'
    },
    normalizeText(value) {
      return (value || '').trim().toLowerCase()
    },
    isSameContact(leftRaw, rightRaw) {
      const left = this.convertContact(leftRaw)
      const right = this.convertContact(rightRaw)

      const isSame = (left, right) => {
        return this.normalizeText(left.firstname) === this.normalizeText(right.firstname)
            && this.normalizeText(left.lastname) === this.normalizeText(right.lastname)
            && this.normalizeText(left.email) === this.normalizeText(right.email)
      }

      if (left.type !== right.type) { return false }
      if (left.type === 'agent') { return left.agentId === right.agentId }
      if (left.type === 'quickAgent') {
        return left.agencyId === right.agencyId && isSame(left, right)
      }
      return isSame(left, right)
    },
    onClickSearchInput() {
      if (this.showQuickForm) {
        this.closeQuickForm()
      }
      if (!this.selectAgentPanel.isActive) { return }
      this.askPanelToConfirmAndClose()
    },
    askPanelToConfirmAndClose() {
      if (this.$refs.selectAgentPanel?.confirmSelection) {
        this.$refs.selectAgentPanel.confirmSelection(false)
      }
    },
    buildAgencyContactsFromSelection(selectionObject) {
      const agency = selectionObject.agency || {}
      const agents = agency.agents || []

      const agentContacts = (selectionObject.selectedIds || []).map(agentId => {
        const sourceAgent = agents.find(agent => agent.id === agentId) || {}
        const existingContact = this.convertedSelectedContacts.find(contact =>
            contact.type === 'agent' &&
            contact.agentId === agentId &&
            contact.agencyId === (agency.id || null)
        ) || {}

        return {
          id: existingContact.id || null,
          workshopId: existingContact.workshopId || this.workshopId || null,
          meetId: existingContact.meetId || null,
          agentId,
          agencyId: agency.id || null,
          agencyName: agency.name || '',
          firstname: sourceAgent.firstname || existingContact.firstname || '',
          lastname: sourceAgent.lastname || existingContact.lastname || '',
          email: sourceAgent.email || existingContact.email || '',
          type: 'agent'
        }
      })

      const quickAgentContacts = (selectionObject.quickAgents || []).map(contact => {
        const existingContact = this.convertedSelectedContacts.find(selectedContact =>
            selectedContact.type === 'quickAgent' &&
            selectedContact.agencyId === (agency.id || null) &&
            this.normalizeText(selectedContact.firstname) === this.normalizeText(contact.firstname) &&
            this.normalizeText(selectedContact.lastname) === this.normalizeText(contact.lastname) &&
            this.normalizeText(selectedContact.email) === this.normalizeText(contact.email)
        ) || {}

        return {
          id: existingContact.id || null,
          workshopId: existingContact.workshopId || this.workshopId || null,
          meetId: existingContact.meetId || null,
          agentId: null,
          agencyId: agency.id || null,
          agencyName: agency.name || '',
          firstname: contact.firstname || '',
          lastname: contact.lastname || '',
          email: contact.email || '',
          type: 'quickAgent'
        }
      })

      return [...agentContacts, ...quickAgentContacts]
    },
    closeselectAgentPanel() {
      this.selectAgentPanel.agencyId = null
      this.selectAgentPanel.agentId = null
      this.selectAgentPanel.isActive = false
    },
    openEditSelectedAgency(selected) {
      this.selectAgentPanel.initialSelection = {
        agency: selected.agency,
        selectedIds: [...selected.selectedIds],
        quickAgents: [...selected.quickAgents]
      }
      this.selectAgentPanel.agentId = null
      this.selectAgentPanel.agencyId = null
      this.selectAgentPanel.isActive = true
    },
    onSelectAgency(agency) {
      const alreadySelected = this.alreadySelectedAgencies.find(s => s.agency.id === agency.id)
      if (alreadySelected) {
        this.openEditSelectedAgency(alreadySelected)
        return
      }
      this.selectAgentPanel.initialSelection = null
      this.selectAgentPanel.agentId = null
      this.selectAgentPanel.agencyId = agency.id
      this.selectAgentPanel.isActive = true
    },
    onSelectAgent(agent) {
      // [N] sélectionner un agent l'ajoute directement (on skip le selectPanel)
      if (agent.agencyId) {
        const alreadySelected = this.alreadySelectedAgencies.find(s => s.agency.id === agent.agencyId)
        // [A] On a déjà l'agence dans nos selected
        if (alreadySelected) {
          this.handleSelection({
                    agency: alreadySelected.agency,
                    selectedIds: [...alreadySelected.selectedIds, agent.id],
                    quickAgents: alreadySelected.quickAgents
                  }, true)
        } else {
          // [B] On fetch l'agence pour l'ajouter dans les selected
          const url = `/api/workshop/agency/${agent.agencyId}`;
          get(url).json((result) => {
            this.handleSelection({
              agency: result,
              selectedIds: [agent.id],
              quickAgents: []
            }, true)
          })
        }
      }
      this.clearSearchBar()
    },
    handleSelection(selectionObject, clearSearch = false) {
      const agencyId = selectionObject?.agency?.id
      if (!agencyId) {
        this.closeselectAgentPanel()
        if (clearSearch) this.clearSearchBar()
        return
      }

      const currentAgencySelection = this.alreadySelectedAgencies.find(sa => sa.agency.id === agencyId)
      const currentContacts = currentAgencySelection
          ? this.buildAgencyContactsFromSelection(currentAgencySelection)
          : []

      const nextContacts = this.buildAgencyContactsFromSelection(selectionObject)

      const toSelect = nextContacts.filter(nextContact =>
          !currentContacts.some(currentContact => this.isSameContact(nextContact, currentContact))
      )

      const toUnselect = currentContacts.filter(currentContact =>
          !nextContacts.some(nextContact => this.isSameContact(currentContact, nextContact))
      )

      if (toSelect.length) {
        this.$emit('selected', toSelect)
      }

      if (toUnselect.length) {
        this.$emit('unselected', toUnselect)
      }

      this.closeselectAgentPanel()
      if (clearSearch) {
        this.clearSearchBar()
      }
    },
    clearSearchBar() {
      if (this.selectAgentPanel.isActive) { return }
      if (this.$refs.smartSearchBar) {
        this.$refs.smartSearchBar.clearInput()
      }
    },
    focusSearchBar() {
      if (this.$refs.smartSearchBar) {
        this.$refs.smartSearchBar.focusSearchInput()
      }
    },
    handleWildQuickAgentCreation() {
      // [N] 'Wild' : sans agence associée
      const wildAgent = this.convertContact({
        firstname: this.quickForm.firstname.trim() || '',
        lastname: this.quickForm.lastname.trim() || '',
        email: this.quickForm.email.trim() || '',
        agencyName: this.quickForm.agencyName.trim() || '',
        type: 'wildAgent'
      })

      this.$emit('selected', [wildAgent])

      this.closeQuickForm()
      this.clearSearchBar()
    },
    openQuickForm() {
      if (this.selectAgentPanel.isActive) { return }
      this.$emit('update:activity', true)
      this.showQuickForm = true
      const query = (this.search?.state.query || "").trim()
      const tokens = query ? query.split(/\s+/) : []
      this.quickForm.firstname = tokens[0] || ""
      this.quickForm.lastname = tokens.slice(1).join(" ") || ""
    },
    closeQuickForm() {
      this.showQuickForm = false
      this.resetQuickForm()
      if (!this.isSearchActive) {
        this.$emit('update:activity', false)
      }
    },
    resetQuickForm() {
      this.showQuickForm = false
      this.quickForm = {
        firstname: "",
        lastname: "",
        email: "",
        agencyName: "",
      }
    },
    reset() {
      if (this.showQuickForm) this.closeQuickForm()
      if (this.selectAgentPanel.isActive) this.closeselectAgentPanel()
      this.clearSearchBar()
    }
  }
}
</script>

<style scoped>
.sas-container {
  width: 100%;
  min-height: 0;
}

.sas-content {
  min-height: 0;
  flex: 1 1 auto;
  width: 100%;
  overflow-y: auto;
}

.sas-quick-form {
  height: 100%;
  width: 100%;
}

.sas-info-panel {
  height: 100%;
  width: 100%;
}

</style>